import invariant from 'tiny-invariant';
import type { RealtimeClient } from './RealtimeClient';
import type { ChannelError, InboundChannelMessage, ChannelEvent, PresenceUser } from './protocol';
import type { ChannelState } from './types';

export interface ChannelOptions {
  onError?: (error: ChannelError) => void;
}

export class Channel {
  private _state: ChannelState = 'unsubscribed';
  private get state(): ChannelState {
    return this._state;
  }
  private set state(s: ChannelState) {
    this._state = s;
    this._notifySubscribers();
  }

  private _presence: PresenceUser[] = [];
  private get presence(): PresenceUser[] {
    return this._presence;
  }
  private set presence(p: PresenceUser[]) {
    this._presence = p;
    this._notifySubscribers();
  }

  private messageListeners: { handler: (message: ChannelEvent<any>) => boolean | void; priority: number }[] = [];

  addMessageListener(handler: (message: ChannelEvent<any>) => boolean | void, priority = 0): () => void {
    const entry = { handler, priority };
    const idx = this.messageListeners.findIndex((l) => l.priority < priority);
    if (idx === -1) this.messageListeners.push(entry);
    else this.messageListeners.splice(idx, 0, entry);
    return () => {
      const i = this.messageListeners.indexOf(entry);
      if (i !== -1) this.messageListeners.splice(i, 1);
    };
  }

  private channelSubscribers = new Set<() => void>();
  private _snapshot: { presence: PresenceUser[]; state: ChannelState } = {
    presence: this._presence,
    state: this._state,
  };

  subscribeToChannel = (callback: () => void): (() => void) => {
    this.channelSubscribers.add(callback);
    return () => this.channelSubscribers.delete(callback);
  };

  getChannelSnapshot = (): { presence: PresenceUser[]; state: ChannelState } => this._snapshot;

  private _notifySubscribers() {
    this._snapshot = { presence: this._presence, state: this._state };
    this.channelSubscribers.forEach((cb) => cb());
  }

  private currentToken?: string;

  // Presence metadata to (re-)send once subscribed. `undefined` = never set, or cleared on
  // an intentional leave (no re-apply); `null` = explicitly cleared via setPresence(null) and
  // is re-applied as `null` on reconnect. Preserved across transient disconnects so a WS blip
  // doesn't drop a user's presence marker while their component state (e.g. focused cell) is
  // unchanged and won't re-fire setPresence.
  private pendingPresence: unknown | undefined;

  // Tracks active useChannel hooks
  private subscriberCount = 0;
  // StrictMode hack
  private unsubscribeTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    public readonly name: string,
    private client: RealtimeClient,
    public options: ChannelOptions = {},
  ) {
    this.name = name;
    this.client = client;
    this.options = options;
  }

  subscribe() {
    if (this.subscriberCount >= 1) {
      throw new Error(
        `[Realtime] Channel "${this.name}" is already joined. Multiple active useChannel ` +
          `hooks for the same channel are not supported. Hoist the channel hook to a ` +
          `shared parent and pass it down via Context.`,
      );
    }

    if (this.unsubscribeTimer) {
      clearTimeout(this.unsubscribeTimer);
      this.unsubscribeTimer = null;
      this.subscriberCount++;
      return;
    }

    this.subscriberCount++;

    this._subscribe();
  }

  private async _subscribe() {
    if (this.state === 'subscribing' || this.state === 'subscribed') return;
    this.state = 'subscribing';

    try {
      if (!this.currentToken) {
        this.currentToken = await this.client.options.getChannelToken(this.name);
      }

      if (this.state !== 'subscribing') return;

      this.client.send({
        type: 'subscribe',
        payload: { channel: this.name, jwt: this.currentToken },
      });
    } catch (err) {
      console.error(`[Realtime] Failed to fetch capability token for ${this.name}`, err);
      this.state = 'unsubscribed'; // Revert state so it can be retried
    }
  }

  unsubscribe() {
    this.subscriberCount = Math.max(0, this.subscriberCount - 1);

    // If we theoretically had >1 allowed, this prevents leaving early.
    // With the throw above, this safely guards against unbalanced unsubscribe() calls.
    if (this.subscriberCount > 0) return;

    this.unsubscribeTimer = setTimeout(() => {
      this.unsubscribeTimer = null;
      this._unsubscribe();
    }, 0);
  }

  /**
   * Full-replace this connection's presence metadata on the channel. `null` clears it.
   * Safe to call regardless of subscription state: the value is remembered and (re-)sent
   * once the channel is (re)subscribed, so a transient disconnect won't silently drop the
   * marker while the consumer's driving state (e.g. focused grid cell) is unchanged.
   */
  setPresence(data: unknown): void {
    this.pendingPresence = data;
    if (this.state !== 'subscribed') return;
    this.client.send({ type: 'set_presence', payload: { channel: this.name, data } });
  }

  private _unsubscribe() {
    if (this.state === 'unsubscribed' || this.state === 'unsubscribing') return;
    this.state = 'unsubscribing';

    this.client.send({ type: 'unsubscribe', payload: { channel: this.name } });
  }

  resubscribe() {
    // Reconnection triggered by the client. Skip channels no active hook wants
    // so we don't resurrect ones whose components have unmounted.
    if (this.subscriberCount === 0) return;
    // A channel mid-subscribe (initial load) or already subscribed needs nothing —
    // its own flow will complete. On a real reconnect handleDisconnect() has already
    // reset us to 'unsubscribed', so we fall through and re-subscribe.
    if (this.state === 'subscribed' || this.state === 'subscribing') return;
    this._subscribe();
  }
  handleDisconnect() {
    this.presence = [];
    this.state = 'unsubscribed';
    // token lives for 60seconds probably expired anway.
    this.currentToken = undefined;
    // Intentionally preserve `pendingPresence`: a transient drop shouldn't clear the
    // marker the consumer still believes is set. It is re-applied on the next `subscribed`.
  }

  handleMessage(message: InboundChannelMessage) {
    invariant(message.payload.channel === this.name);
    switch (message.type) {
      case 'error':
        if (message.payload.code === 'TOKEN_EXPIRED') {
          console.log(`[Realtime] Token expired for ${this.name}. Refetching automatically...`);
          this.currentToken = undefined; // Clear the bad token
          this.state = 'unsubscribed';
          this._subscribe(); // Try again (will hit the promise to fetch a new one)
        } else if (message.payload.code === 'NOT_SUBSCRIBED') {
          // Our local state disagrees with the server (e.g. a set_presence raced with a
          // dropped subscription). Recover by resubscribing; pendingPresence re-applies on
          // the subsequent `subscribed`. Swallowed so consumers don't see a hard failure for
          // what is a transient race.
          console.warn(`[Realtime] NOT_SUBSCRIBED for ${this.name}; resubscribing to recover.`);
          this.state = 'unsubscribed';
          this._subscribe();
        } else {
          this.options.onError?.(message as ChannelError);
        }
        break;
      case 'subscribed':
        this.presence = message.payload.presence;
        this.state = 'subscribed';
        // Re-apply stored presence on (re)subscribe. `undefined` = never set / intentionally
        // left → skip; `null` is a valid explicit clear → re-send as null.
        if (this.pendingPresence !== undefined) {
          this.setPresence(this.pendingPresence);
        }
        break;
      case 'unsubscribed':
        this.presence = [];
        this.state = 'unsubscribed';
        this.currentToken = undefined; // Clear token on manual unsubs
        this.pendingPresence = undefined; // We left; don't resurrect presence on a later resubscribe
        break;
      case 'join':
        this.presence = [...this._presence, message.payload.user];
        break;
      case 'leave':
        this.presence = this._presence.filter((u) => u.connection_id !== message.payload.user.connection_id);
        break;
      case 'presence_update':
        // Replace the member in place by connection_id. A user may hold multiple connections;
        // each updates independently, matching how join/leave key on connection_id.
        this.presence = this._presence.map((u) =>
          u.connection_id === message.payload.user.connection_id ? message.payload.user : u,
        );
        break;
      case 'message':
        for (const { handler } of this.messageListeners) {
          if (handler(message) === true) break;
        }
        break;
    }
  }
}
